import { useCallback, useState } from 'react'
import type { ChatMapOnMapData } from 'tripPlanner/assistant/mapTypes'
import {
  createChatMapOnMapMarkers,
  type MapCoordinate,
  type MapMarkerComponent,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { RightPanelView } from './useRightPanelState'
import { PLACE_SHOW_ON_MAP_ZOOM } from '../styles/layoutStyles'

interface UseChatMapOnMapMarkersParams {
  hideHotelFilter: () => void
  hideExperienceFilter: () => void
  handleExploreCategorySelect: (category: undefined) => void
  clearCruiseTransportMapState: () => void
  clearOfferPlaceSuggestionMarkers: () => void
  setRightPanelView: (view: RightPanelView) => void
  moveMapTo: (lat: number, lng: number, zoom: number) => void
  requestFitToCoordinates: (coordinates: Array<MapCoordinate>) => void
  clearFitBoundsRequest: () => void
}

interface UseChatMapOnMapMarkersReturn {
  chatMapMarkers: Array<MapMarkerComponent>
  clearChatMapMarkers: () => void
  handleChatMapOnMapClick: (data: ChatMapOnMapData) => void
}

export function useChatMapOnMapMarkers({
  hideHotelFilter,
  hideExperienceFilter,
  handleExploreCategorySelect,
  clearCruiseTransportMapState,
  clearOfferPlaceSuggestionMarkers,
  setRightPanelView,
  moveMapTo,
  requestFitToCoordinates,
  clearFitBoundsRequest,
}: UseChatMapOnMapMarkersParams): UseChatMapOnMapMarkersReturn {
  const [chatMapMarkers, setChatMapMarkers] = useState<Array<MapMarkerComponent>>([])

  const clearChatMapMarkers = useCallback(() => {
    setChatMapMarkers([])
    clearFitBoundsRequest()
  }, [clearFitBoundsRequest])

  const handleChatMapOnMapClick = useCallback((data: ChatMapOnMapData) => {
    const markers = createChatMapOnMapMarkers(data.pins)
    hideHotelFilter()
    hideExperienceFilter()
    handleExploreCategorySelect(undefined)
    clearCruiseTransportMapState()
    clearOfferPlaceSuggestionMarkers()
    setChatMapMarkers(markers)
    setRightPanelView('map')

    if (markers.length === 0) {
      clearFitBoundsRequest()
      return
    }

    if (markers.length === 1) {
      const [marker] = markers
      clearFitBoundsRequest()
      moveMapTo(
        marker.lat,
        marker.lng,
        typeof data.zoom === 'number' && Number.isFinite(data.zoom) ? data.zoom : PLACE_SHOW_ON_MAP_ZOOM,
      )
      return
    }

    requestFitToCoordinates(markers)
  }, [
    clearCruiseTransportMapState,
    clearFitBoundsRequest,
    clearOfferPlaceSuggestionMarkers,
    handleExploreCategorySelect,
    hideExperienceFilter,
    hideHotelFilter,
    moveMapTo,
    requestFitToCoordinates,
    setRightPanelView,
  ])

  return {
    chatMapMarkers,
    clearChatMapMarkers,
    handleChatMapOnMapClick,
  }
}
